// Shared validation-log view — one stream, three lenses (Sales/Ops/Audit).
// Data: /analytics/validations-log. Tabs: All / Denied.
import React, { useEffect, useMemo, useState } from 'react';
import { PageHeader, Card, CardHead, Table, Td, Row, Mono, Btn } from '@/components/shared/primitives';
import { Kpi } from '@/components/shared/Kpi';
import { Pill } from '@/components/shared/badges';
import { EmptyState } from '@/components/shared/EmptyState';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { CompanyChip } from '@/components/shared/CompanyChip';
import { MaskedKey } from '@/components/shared/MaskedKey';
import { formatRelativeTime } from '@/lib/utils';
import { getValidationsLog, getValidationsRollup, type ValidationLogRow, type ValidationRollup } from '@/lib/api/audit';

export function ValidationsView({ title, subtitle, summary }: { title: string; subtitle: string; summary?: React.ReactNode }) {
  const [rows, setRows] = useState<ValidationLogRow[]>([]);
  const [denied, setDenied] = useState<ValidationLogRow[]>([]);
  const [tab, setTab] = useState<'all' | 'denied'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch the recent stream AND the denied set separately: raw successes are only
    // retained ~30 days while denials are retained long, so a single recent page
    // would hide older denials. The server derives valid from the scalar column.
    Promise.all([
      getValidationsLog({ limit: 300 }),
      getValidationsLog({ denied: true, limit: 500 }),
    ])
      .then(([all, den]) => { setRows(all); setDenied(den); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load validations'))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => (tab === 'denied' ? denied : rows), [rows, denied, tab]);
  const tabs: Array<['all' | 'denied', string, number]> = [['all', 'Recent', rows.length], ['denied', 'Denied', denied.length]];

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} actions={<Btn icon="download">Export</Btn>} />
      {summary}
      <div style={{ display: 'flex', gap: 2, background: 'var(--bg-muted)', padding: 3, borderRadius: 8, width: 'fit-content', marginBottom: 16 }}>
        {tabs.map(([k, l, n]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: tab === k ? '#fff' : 'transparent', color: tab === k ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: 12.5, fontWeight: tab === k ? 600 : 500, cursor: 'pointer', boxShadow: tab === k ? 'var(--shadow-card)' : 'none', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
            {l}<span style={{ padding: '1px 6px', borderRadius: 99, background: k === 'denied' && n > 0 ? '#FEE2E2' : 'var(--bg-muted)', color: k === 'denied' && n > 0 ? '#DC2626' : 'var(--text-muted)', fontSize: 10, fontWeight: 700 }}>{n}</span>
          </button>
        ))}
      </div>
      {error && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">{error}</AlertBanner></div>}
      <Card style={{ overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div> : visible.length === 0 ? (
          <EmptyState icon="badge-check" title="No validations" body="License validation attempts (successes and denials) will appear here." />
        ) : (
          <Table cols={[{ label: 'Timestamp' }, { label: 'Result' }, { label: 'License' }, { label: 'Account' }, { label: 'Source IP' }]}>
            {visible.slice(0, 200).map((v) => (
              <Row key={v.id}>
                <Td><Mono style={{ fontSize: 12 }}>{new Date(v.timestamp).toLocaleString()}</Mono></Td>
                <Td>{v.success ? <Pill bg="#DCFCE7" fg="#15803D" dot>valid</Pill> : <Pill bg="#FEE2E2" fg="#DC2626" dot>denied</Pill>}</Td>
                <Td><MaskedKey value={v.license_key} size={12} /></Td>
                <Td><CompanyChip id={v.org_id} name={v.org_name} origin={{ type: 'validations' }} icon={false} /></Td>
                <Td><Mono style={{ color: 'var(--text-muted)' }}>{v.ip_address}</Mono></Td>
              </Row>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

// Audit rollup summary — reads the permanent validation_daily rollup (migration 23):
// per-day valid/denied trend + per-account totals over a window. This is the audit
// lens (proof checks happened + denial spikes), decoupled from the raw stream below.
function ValidationRollupSummary() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ValidationRollup | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    getValidationsRollup(days).then((d) => { if (live) setData(d); }).catch(() => { if (live) setData(null); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [days]);

  const t = data?.totals;
  const maxDay = Math.max(1, ...(data?.series || []).map((s) => s.valid + s.denied));

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Rolled-up summary</div>
        <div style={{ display: 'flex', gap: 2, background: 'var(--bg-muted)', padding: 3, borderRadius: 8 }}>
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: days === d ? '#fff' : 'transparent', color: days === d ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: 12, fontWeight: days === d ? 600 : 500, cursor: 'pointer', boxShadow: days === d ? 'var(--shadow-card)' : 'none', fontFamily: 'inherit' }}>{d}d</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 16 }}>
        <Kpi label={`Validations (${days}d)`} value={loading ? '…' : (t?.valid ?? 0).toLocaleString()} accent="#7C3AED" icon="badge-check" />
        <Kpi label="Denials" value={loading ? '…' : (t?.denied ?? 0).toLocaleString()} accent={t && t.denied > 0 ? '#DC2626' : '#7C3AED'} icon="shield-alert" />
        <Kpi label="Denial rate" value={loading ? '…' : `${t?.denial_rate ?? 0}%`} accent="#7C3AED" icon="percent" />
        <Kpi label="Accounts checked" value={loading ? '…' : (t?.accounts ?? 0).toLocaleString()} accent="#7C3AED" icon="building-2" />
      </div>

      {/* per-day trend: stacked valid (green) + denied (red) */}
      <Card style={{ marginBottom: 16 }}>
        <CardHead title="Daily validations" sub={`Valid vs denied per day · last ${days} days (from the daily rollup)`} />
        <div style={{ padding: '16px 20px' }}>
          {(!data || data.series.length === 0) ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No rolled-up data in this window yet.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120 }}>
              {data.series.map((s) => {
                const total = s.valid + s.denied;
                const h = Math.max(2, Math.round((total / maxDay) * 108));
                const deniedH = total > 0 ? Math.round((s.denied / total) * h) : 0;
                return (
                  <div key={s.day} title={`${s.day}: ${s.valid} valid, ${s.denied} denied`} style={{ flex: 1, minWidth: 4, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                    {deniedH > 0 && <div style={{ height: deniedH, background: '#DC2626', borderRadius: '2px 2px 0 0' }} />}
                    <div style={{ height: h - deniedH, background: '#16A34A', borderRadius: deniedH > 0 ? 0 : '2px 2px 0 0' }} />
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11.5, color: 'var(--text-muted)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: '#16A34A' }} />valid</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: '#DC2626' }} />denied</span>
          </div>
        </div>
      </Card>

      {/* per-account totals */}
      <Card style={{ overflow: 'hidden', marginBottom: 4 }}>
        <CardHead title="By account" sub="Validation totals per company over the window" />
        {(!data || data.accounts.length === 0) ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No accounts validated in this window.</div>
        ) : (
          <Table cols={[{ label: 'Account' }, { label: 'Valid', align: 'right' }, { label: 'Denied', align: 'right' }, { label: 'Last seen' }]}>
            {data.accounts.map((a, i) => (
              <Row key={a.organization_id || i}>
                <Td><CompanyChip id={a.organization_id} name={a.org_name} origin={{ type: 'validations' }} icon={false} /></Td>
                <Td align="right"><Mono>{a.valid.toLocaleString()}</Mono></Td>
                <Td align="right">{a.denied > 0 ? <Pill bg="#FEE2E2" fg="#DC2626">{a.denied.toLocaleString()}</Pill> : <Mono style={{ color: 'var(--text-muted)' }}>0</Mono>}</Td>
                <Td style={{ color: 'var(--text-secondary)' }}>{a.last_seen ? formatRelativeTime(a.last_seen) : '—'}</Td>
              </Row>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

export function AuditValidations() {
  return (
    <ValidationsView
      title="Validation Logs"
      subtitle="Rolled-up validation history + the raw attempt stream"
      summary={<ValidationRollupSummary />}
    />
  );
}
export default ValidationsView;
